export const INVALID_AUTH_TOKEN_MESSAGE = 'Invalid or expired authentication token';

type SessionInvalidatedListener = () => void;

const listeners: SessionInvalidatedListener[] = [];
let sessionInvalidated = false;

const AUTH_FAILURE_PATTERNS = [
  'invalid or expired authentication token',
  'authentication check failed',
  'jwt expired',
  'jwt malformed',
  'invalid token',
  'unauthorized',
  'forbidden',
];

export function isInvalidAuthTokenMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  if (!normalized) return false;
  return AUTH_FAILURE_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export async function extractErrorMessageFromResponse(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return '';
    try {
      const data = JSON.parse(text);
      return String(data?.error || data?.message || text);
    } catch {
      return text;
    }
  } catch {
    return '';
  }
}

export function reportInvalidAuthIfNeeded(status: number, message: string): void {
  if ((status === 401 || status === 403) && isInvalidAuthTokenMessage(message)) {
    notifySessionInvalidated();
    return;
  }
  if (isInvalidAuthTokenMessage(message)) {
    notifySessionInvalidated();
  }
}

export function reportInvalidAuthFromError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (isInvalidAuthTokenMessage(message)) {
    notifySessionInvalidated();
  }
}

export function notifySessionInvalidated(): void {
  if (sessionInvalidated) return;
  sessionInvalidated = true;
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  }
}

export function onSessionInvalidated(callback: SessionInvalidatedListener): () => void {
  listeners.push(callback);
  return () => {
    const index = listeners.indexOf(callback);
    if (index >= 0) listeners.splice(index, 1);
  };
}

export function resetSessionInvalidation(): void {
  sessionInvalidated = false;
}

export function isSessionInvalidated(): boolean {
  return sessionInvalidated;
}
